import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  dismissOnboardingVideos,
  getOnboardingVideos,
  markOnboardingVideoWatched,
  OnboardingSeriesResponse,
} from '../../../redux/actions/onboardingVideosAction'
import OnboardingVideoSeriesView from './OnboardingVideoSeriesView'
import { seriesRequestedByLink, shouldShowSeries } from './seriesVisibility'

/**
 * HSK 1 video series shown on /stories. Self-contained: it fetches its own eligibility/state and
 * renders nothing unless the learner reported "never learned" during onboarding and hasn't
 * dismissed it, or opened a link with ?series=hsk1 (see seriesVisibility).
 */
const OnboardingVideoSeries: React.FC = () => {
  const { t } = useTranslation('landing')
  const [data, setData] = useState<OnboardingSeriesResponse | null>(null)
  const [hidden, setHidden] = useState(false)
  const [markingLessonId, setMarkingLessonId] = useState<string | null>(null)
  // The lesson just marked in this session. It keeps its card on screen — with
  // the confirmation and a Continue button — instead of the series silently
  // jumping to the next lesson the moment the words are saved.
  const [justWatchedId, setJustWatchedId] = useState<string | null>(null)
  const [markErrorById, setMarkErrorById] = useState<Record<string, string>>({})

  useEffect(() => {
    let active = true
    getOnboardingVideos().then((res) => {
      if (active) setData(res)
    })
    return () => {
      active = false
    }
  }, [])

  const requestedByLink = seriesRequestedByLink(window.location.search)
  if (!data || !shouldShowSeries({ eligible: data.eligible, dismissed: data.dismissed, hiddenThisVisit: hidden, requestedByLink })) {
    return null
  }

  const handleMarkWatched = async (lessonId: string) => {
    setMarkingLessonId(lessonId)
    setMarkErrorById((prev) => ({ ...prev, [lessonId]: '' }))
    try {
      await markOnboardingVideoWatched(lessonId)
      setJustWatchedId(lessonId)
      setData((prev) =>
        prev
          ? { ...prev, lessons: prev.lessons.map((l) => (l.id === lessonId ? { ...l, watched: true } : l)) }
          : prev,
      )
    } catch {
      setMarkErrorById((prev) => ({ ...prev, [lessonId]: t('videoSeries.markError') }))
    } finally {
      setMarkingLessonId(null)
    }
  }

  const handleDismiss = () => {
    // Optimistically hide; persist in the background so it stays hidden next visit.
    setHidden(true)
    void dismissOnboardingVideos().catch(() => undefined)
  }

  return (
    <OnboardingVideoSeriesView
      series={data.series}
      lessons={data.lessons}
      markingLessonId={markingLessonId}
      markErrorById={markErrorById}
      justWatchedId={justWatchedId}
      onMarkWatched={handleMarkWatched}
      onContinue={() => setJustWatchedId(null)}
      onDismiss={handleDismiss}
    />
  )
}

export default OnboardingVideoSeries
